/**
 * Modular PDP add-ons — selectable upsell rows with cart add/remove.
 * Requires window.NNHCart (nnh-atc.js). Init via [data-modular-pdp-addons].
 */
(function (win, doc) {
  'use strict';

  if (win.ModularPdpAddOns) return;

  function formatMoney(cents) {
    if (win.Shopify && typeof win.Shopify.formatMoney === 'function') {
      return win.Shopify.formatMoney(
        cents,
        win.theme && win.theme.moneyFormat ? win.theme.moneyFormat : undefined
      );
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function initSection(section) {
    if (section.dataset.addonsInit === 'true') return;
    section.dataset.addonsInit = 'true';

    var cartUrl = section.dataset.cartUrl;
    var cartChangeUrl = section.dataset.cartChangeUrl;
    if (!cartUrl || !cartChangeUrl) return;

    var items = Array.prototype.slice.call(section.querySelectorAll('[data-addon-item]'));
    var summary = section.querySelector('[data-addons-summary]');
    var countEl = section.querySelector('[data-addons-count]');
    var totalEl = section.querySelector('[data-addons-total]');
    if (!items.length || !summary || !countEl || !totalEl) return;

    function showError(message) {
      if (win.NNHCart && typeof win.NNHCart.showToast === 'function') {
        win.NNHCart.showToast(message, 'error');
      }
    }

    function findLineKey(cart, item) {
      var variantId = Number(item.dataset.addonVariantId || 0);
      if (!variantId) return '';

      var match = (cart.items || []).find(function (line) {
        if (Number(line.variant_id || 0) !== variantId) return false;
        var lineSellingPlan = Number(
          line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
            ? line.selling_plan_allocation.selling_plan.id
            : 0
        );
        return lineSellingPlan === 0;
      });
      return match && match.key ? match.key : '';
    }

    function syncFromCart(cart) {
      items.forEach(function (item) {
        var lineKey = findLineKey(cart, item);
        if (lineKey) {
          item.dataset.addonLineKey = lineKey;
          setSelectedState(item, true);
        } else {
          delete item.dataset.addonLineKey;
          setSelectedState(item, false);
        }
      });
      updateSummary();
    }

    function fetchCartJson() {
      return fetch(cartUrl + '.js', { method: 'GET' }).then(function (response) {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      });
    }

    function refreshFromCart() {
      return fetchCartJson()
        .then(syncFromCart)
        .catch(function () {});
    }

    function updateSummary() {
      var selected = items.filter(function (item) {
        return item.classList.contains('is-selected');
      });
      var count = selected.length;
      var total = selected.reduce(function (sum, item) {
        return sum + Number(item.dataset.addonPriceCents || 0);
      }, 0);

      countEl.textContent = count + ' add-on' + (count === 1 ? '' : 's') + ' selected';
      totalEl.textContent = '+' + formatMoney(total);
      summary.hidden = count === 0;
    }

    function addAddonToCart(item) {
      var variantId = Number(item.dataset.addonVariantId || 0);
      if (!variantId) {
        showError('Unable to add this add-on right now.');
        return Promise.resolve(false);
      }
      if (!win.NNHCart) {
        showError('Unable to add this add-on right now.');
        return Promise.resolve(false);
      }
      return new Promise(function (resolve) {
        win.NNHCart.addItem({
          id: variantId,
          selling_plan: '',
          quantity: 1,
          button: null,
          silent: false,
          onSuccess: function (addedItem) {
            if (addedItem && addedItem.key) {
              item.dataset.addonLineKey = addedItem.key;
            } else {
              fetchCartJson().then(function (cart) {
                var key = findLineKey(cart, item);
                if (key) item.dataset.addonLineKey = key;
              });
            }
            resolve(true);
          },
          onError: function () {
            resolve(false);
          }
        });
      });
    }

    function removeAddonFromCart(item) {
      var variantId = Number(item.dataset.addonVariantId || 0);
      if (!variantId) return Promise.resolve(false);

      var lineKey = item.dataset.addonLineKey || '';

      function changeLine(keyOrIndex, useLineIndex) {
        var body = useLineIndex
          ? JSON.stringify({ line: keyOrIndex, quantity: 0 })
          : JSON.stringify({ id: keyOrIndex, quantity: 0 });
        return fetch(cartChangeUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body
        });
      }

      return fetchCartJson()
        .then(function (cart) {
          if (!lineKey) {
            lineKey = findLineKey(cart, item);
            if (lineKey) item.dataset.addonLineKey = lineKey;
          }
          return lineKey;
        })
        .then(function (resolvedKey) {
          if (!resolvedKey) return false;

          return changeLine(resolvedKey, false).then(function (response) {
            if (response.ok) return true;

            return fetchCartJson().then(function (cartRetry) {
              var lineIndex = (cartRetry.items || []).findIndex(function (line) {
                return line.key === resolvedKey;
              });
              if (lineIndex < 0) throw new Error('Cart remove failed');
              return changeLine(lineIndex + 1, true).then(function (retryResponse) {
                if (!retryResponse.ok) throw new Error('Cart remove failed');
                return true;
              });
            });
          });
        })
        .then(function (removed) {
          if (removed) {
            delete item.dataset.addonLineKey;
            doc.documentElement.dispatchEvent(
              new CustomEvent('cart:refresh', { detail: { source: 'modular-pdp-addons' } })
            );
          }
          return removed;
        });
    }

    function clearCheckTimer(item) {
      if (item._addonCheckTimer) {
        win.clearTimeout(item._addonCheckTimer);
        item._addonCheckTimer = null;
      }
    }

    function showAddedConfirmation(item) {
      clearCheckTimer(item);
      item.classList.add('is-checking');
      item._addonCheckTimer = win.setTimeout(function () {
        item.classList.remove('is-checking');
        item._addonCheckTimer = null;
      }, 1200);
    }

    function setSelectedState(item, selected) {
      clearCheckTimer(item);
      item.classList.toggle('is-selected', selected);
      item.classList.remove('is-checking');
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      item.setAttribute(
        'aria-label',
        (item.dataset.addonName || 'Add-on') +
          (selected ? ' — remove from cart' : ' — add to cart')
      );
    }

    items.forEach(function (item) {
      item.addEventListener('click', function () {
        if (item.classList.contains('is-adding') || item.classList.contains('is-removing')) return;

        var isSelected = item.classList.contains('is-selected');

        if (isSelected) {
          item.classList.add('is-removing');
          item.setAttribute('aria-busy', 'true');
          removeAddonFromCart(item)
            .then(function (removed) {
              if (!removed) {
                showError('Could not remove add-on. Please try again.');
                return;
              }
              setSelectedState(item, false);
              updateSummary();
            })
            .catch(function () {
              showError('Could not remove add-on. Please try again.');
            })
            .finally(function () {
              item.classList.remove('is-removing');
              item.removeAttribute('aria-busy');
            });
          return;
        }

        item.classList.add('is-adding');
        item.setAttribute('aria-busy', 'true');
        addAddonToCart(item)
          .then(function (added) {
            if (!added) return;
            setSelectedState(item, true);
            showAddedConfirmation(item);
            updateSummary();
          })
          .catch(function () {
            showError('Could not add add-on. Please try again.');
          })
          .finally(function () {
            item.classList.remove('is-adding');
            item.removeAttribute('aria-busy');
          });
      });
    });

    refreshFromCart();

    function getShopifyCartFromEvent(event) {
      if (!event || !event.detail) return null;

      if (event.detail.cart) {
        if (event.detail.cart.cart && event.detail.cart.cart.items) {
          return event.detail.cart.cart;
        }
        if (event.detail.cart.items) {
          return event.detail.cart;
        }
      }

      return null;
    }

    function getRebuyShopifyCart() {
      if (
        win.Rebuy &&
        win.Rebuy.SmartCart &&
        win.Rebuy.SmartCart.cart &&
        win.Rebuy.SmartCart.cart.items
      ) {
        return win.Rebuy.SmartCart.cart;
      }
      return null;
    }

    function onCartUpdated(event) {
      if (event && event.detail && event.detail.source === 'modular-pdp-addons') return;

      var shopifyCart = getShopifyCartFromEvent(event) || getRebuyShopifyCart();
      if (shopifyCart) {
        syncFromCart(shopifyCart);
        return;
      }

      refreshFromCart();
    }

    doc.addEventListener('cart:updated', onCartUpdated);
    doc.addEventListener('cart:refresh', onCartUpdated);
    doc.documentElement.addEventListener('cart:refresh', onCartUpdated);
    doc.documentElement.addEventListener('cart:updated', onCartUpdated);
    doc.addEventListener('cart:item-added', onCartUpdated);
    doc.addEventListener('rebuy:cart.change', onCartUpdated);
    doc.addEventListener('rebuy:cart.updated', onCartUpdated);
    doc.addEventListener('rebuy:smartcart.hide', onCartUpdated);
  }

  function initAll() {
    var sections = doc.querySelectorAll('[data-modular-pdp-addons]');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  win.ModularPdpAddOns = { init: initAll, initSection: initSection };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})(window, document);
